import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import PushNotificationToggle from '../../components/PushNotificationToggle';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  Bell, 
  User,
  Plus,
  Loader2,
  Search,
  X,
  MessageCircle,
  Star,
  Send,
  Gift,
  Copy,
  Video,
  Settings,
  Receipt,
  Shield,
  BellRing,
  FileText,
  Trophy,
  ArrowRight,
  Mic
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AccountDeletionButton } from '../../components/AccountDeletionButton';
import { DataExportButton } from '../../components/DataExportButton';
import ForumCommunity from '../../components/ForumCommunity';
import AdBanner from '../../components/AdBanner';
import DeviceTestModal from '../../components/DeviceTestModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PatientDashboard = () => {
  const { t, language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [isTeleconsultation, setIsTeleconsultation] = useState(false);
  const [showDeviceTest, setShowDeviceTest] = useState(false);

  const [waitlistData, setWaitlistData] = useState({
    professional_id: '',
    preferred_date_start: '',
    preferred_date_end: '',
    preferred_time_start: '08:00',
    preferred_time_end: '18:00'
  });

  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
    is_anonymous: false
  });

  const [loadingSlots, setLoadingSlots] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, waitlistRes, professionalsRes, conversationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, { headers }),
        axios.get(`${API_URL}/api/waitlist`, { headers }),
        axios.get(`${API_URL}/api/professionals`),
        axios.get(`${API_URL}/api/chat/conversations`, { headers }).catch(() => ({ data: [] }))
      ]);
      setAppointments(appointmentsRes.data);
      setWaitlist(waitlistRes.data);
      setProfessionals(professionalsRes.data);
      setConversations(conversationsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchAvailability = async (professionalId, date) => {
    setLoadingSlots(true);
    setAvailability([]);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_URL}/api/availability/${professionalId}?date=${dateStr}`,
        { headers }
      );
      const slots = response.data[0]?.slots || [];
      setAvailability(slots);
    } catch (error) {
      console.error('Availability error:', error);
      setAvailability([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSelectProfessional = (professional) => {
    setSelectedProfessional(professional);
    setIsTeleconsultation(false);
    fetchAvailability(professional.id, selectedDate);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (selectedProfessional) {
      fetchAvailability(selectedProfessional.id, date);
    }
  };

  const handleBookAppointment = async (slot) => {
    try {
      // Calculate end time (30 min after start)
      const [hours, minutes] = slot.time.split(':').map(Number);
      const endHours = minutes >= 30 ? hours + 1 : hours;
      const endMinutes = minutes >= 30 ? minutes - 30 : minutes + 30;
      const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
      
      await axios.post(
        `${API_URL}/api/appointments`,
        {
          professional_id: selectedProfessional.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          start_time: slot.time,
          end_time: endTime,
          reason: 'Consultation',
          is_teleconsultation: isTeleconsultation
        },
        { headers }
      );
      toast.success(t('appointment_booked'));
      setShowBooking(false);
      setSelectedProfessional(null);
      setAvailability([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    try {
      await axios.patch(`${API_URL}/api/appointments/${appointmentId}/cancel`, {}, { headers });
      toast.success(t('success'));
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleJoinWaitlist = async () => {
    try {
      await axios.post(`${API_URL}/api/waitlist`, waitlistData, { headers });
      toast.success(t('success'));
      setShowWaitlist(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleLeaveWaitlist = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/api/waitlist/${entryId}`, { headers });
      toast.success(t('success'));
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedAppointment) return;
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        professional_id: selectedAppointment.professional_id,
        appointment_id: selectedAppointment.id,
        ...reviewData
      }, { headers });
      toast.success('Avis envoyé !');
      setShowReview(false);
      setSelectedAppointment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const openChat = async (userId) => {
    setSelectedChatUser(userId);
    setShowChat(true);
    try {
      const response = await axios.get(`${API_URL}/api/chat/conversation/${userId}`, { headers });
      setChatMessages(response.data);
    } catch (error) {
      console.error('Chat error:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChatUser) return;
    try {
      await axios.post(`${API_URL}/api/chat/send`, {
        recipient_id: selectedChatUser,
        content: newMessage
      }, { headers });
      setNewMessage('');
      // Refresh messages
      const response = await axios.get(`${API_URL}/api/chat/conversation/${selectedChatUser}`, { headers });
      setChatMessages(response.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Code copié !');
  };

  const filteredProfessionals = professionals.filter(p =>
    p.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.profile?.specialty?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: 'default',
      confirmed: 'secondary',
      cancelled: 'destructive',
      completed: 'outline'
    };
    return <Badge variant={variants[status] || 'default'}>{t(`status_${status}`)}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            {t('dashboard_welcome')}, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">{t('dashboard_appointments')} & {t('dashboard_waitlist')}</p>
          
          {/* Push Notifications + Trial Badge */}
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <PushNotificationToggle token={token} />
            {user?.trial_ends_at && (
              <span className="trial-badge">
                {t('pricing_trial')} - Expire le {new Date(user.trial_ends_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Search Doctor CTA */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/professionals')}
                data-testid="search-doctor-cta">
            <CardContent className="p-5 flex items-center gap-4 text-white">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-lg">{t('search_professional')}</p>
                <p className="text-blue-100 text-sm">{t('specialty')}, {t('city')}, {t('country')}</p>
              </div>
              <ArrowRight className="w-5 h-5 ml-auto" />
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 border-0 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/professionals?voice=1')}
                data-testid="voice-search-cta">
            <CardContent className="p-5 flex items-center gap-4 text-white">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Mic className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-lg">{t('voice.title')}</p>
                <p className="text-emerald-100 text-sm">{t('voice.description')}</p>
              </div>
              <ArrowRight className="w-5 h-5 ml-auto" />
            </CardContent>
          </Card>
        </div>

        {/* Referral Card */}
        <Card className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="p-4">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="font-semibold text-slate-900">{t('referral_title')}</p>
                  <p className="text-sm text-slate-600">{t('referral_desc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-3 py-1 rounded-lg font-mono text-primary">
                  {user?.referral_code}
                </code>
                <Button size="icon" variant="ghost" onClick={copyReferralCode}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="appointments">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="appointments" className="gap-2">
              <Calendar className="w-4 h-4" />
              RDV
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="gap-2">
              <Bell className="w-4 h-4" />
              Attente
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              Messages
              {conversations.reduce((acc, c) => acc + c.unread_count, 0) > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {conversations.reduce((acc, c) => acc + c.unread_count, 0)}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="forum" className="gap-2" data-testid="tab-forum">
              <MessageCircle className="w-4 h-4" />
              Forum
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        {t('dashboard_appointments')}
                      </CardTitle>
                      <CardDescription>Vos rendez-vous à venir</CardDescription>
                    </div>
                    <Dialog open={showBooking} onOpenChange={setShowBooking}>
                      <DialogTrigger asChild>
                        <Button className="gap-2" data-testid="new-appointment-btn" onClick={(e) => {
                          e.preventDefault();
                          // Scroll to search CTA buttons at top of page
                          const searchCta = document.querySelector('[data-testid="search-doctor-cta"]');
                          if (searchCta) {
                            searchCta.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            searchCta.classList.add('ring-4', 'ring-blue-300');
                            setTimeout(() => searchCta.classList.remove('ring-4', 'ring-blue-300'), 2000);
                          } else {
                            navigate('/professionals');
                          }
                        }}>
                          <Plus className="w-4 h-4" />
                          {t('appointment_book')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        <DialogHeader className="flex-shrink-0 pb-2 border-b">
                          <DialogTitle>{t('appointment_book')}</DialogTitle>
                        </DialogHeader>
                        <div 
                          className="flex-1 overflow-y-auto overscroll-contain touch-pan-y"
                          style={{ 
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'thin',
                            msOverflowStyle: 'none'
                          }}
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 pb-6 px-1">
                          {/* Professional Selection */}
                          <div className="space-y-4">
                            <div className="relative">
                              <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                              <Input
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={isRTL ? 'pr-10' : 'pl-10'}
                              />
                            </div>
                            <ScrollArea className="h-60">
                              <div className="space-y-2">
                                {filteredProfessionals.map((pro) => (
                                  <div
                                    key={pro.id}
                                    data-testid={`select-pro-${pro.id}`}
                                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                      selectedProfessional?.id === pro.id 
                                        ? 'border-primary bg-primary/5' 
                                        : 'border-slate-200 hover:border-primary/50'
                                    }`}
                                    onClick={() => handleSelectProfessional(pro)}
                                  >
                                    <p className="font-medium">Dr. {pro.first_name} {pro.last_name}</p>
                                    <p className="text-sm text-slate-500">{pro.profile?.specialty}</p>
                                    {pro.profile?.city && (
                                      <p className="text-xs text-slate-400">📍 {pro.profile.city}</p>
                                    )}
                                    {pro.average_rating && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                        <span className="text-xs">{pro.average_rating.toFixed(1)}</span>
                                      </div>
                                    )}
                                    {pro.profile?.teleconsultation_enabled && (
                                      <Badge variant="outline" className="mt-1 text-xs">
                                        <Video className="w-3 h-3 mr-1" /> Téléconsult
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          </div>

                          {/* Calendar & Slots */}
                          <div className="space-y-4">
                            {selectedProfessional?.profile?.teleconsultation_enabled && (
                              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                                <input
                                  type="checkbox"
                                  id="teleconsult"
                                  checked={isTeleconsultation}
                                  onChange={(e) => setIsTeleconsultation(e.target.checked)}
                                />
                                <Label htmlFor="teleconsult" className="text-sm cursor-pointer">
                                  <Video className="w-4 h-4 inline mr-1" />
                                  Téléconsultation
                                </Label>
                              </div>
                            )}
                            <CalendarComponent
                              mode="single"
                              selected={selectedDate}
                              onSelect={handleDateChange}
                              className="rounded-md border"
                              disabled={(date) => date < new Date()}
                            />
                            
                            {selectedProfessional && (
                              <div className="space-y-2">
                                <p className="text-sm font-medium">{t('appointment_time')}</p>
                                
                                {/* Loading indicator */}
                                {loadingSlots && (
                                  <div className="flex items-center justify-center py-6">
                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                    <span className="ml-2 text-sm text-slate-500">{t('loading_slots')}</span>
                                  </div>
                                )}
                                
                                {/* Time slots grid */}
                                {!loadingSlots && (
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {availability.filter(s => s.available).map((slot, idx) => (
                                      <Button
                                        key={idx}
                                        variant="outline"
                                        size="sm"
                                        className="slot-available hover:bg-primary hover:text-white transition-colors"
                                        onClick={() => handleBookAppointment(slot)}
                                        data-testid={`slot-${slot.time}`}
                                      >
                                        🕐 {slot.time}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                                
                                {/* No slots message */}
                                {!loadingSlots && availability.filter(s => s.available).length === 0 && (
                                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                                    <p className="text-sm text-slate-500">
                                      {t('no_slots')}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                      Essayez une autre date
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {appointments.filter(a => a.status !== 'cancelled').map((apt) => {
                        const professional = professionals.find(p => p.id === apt.professional_id);
                        return (
                          <div 
                            key={apt.id}
                            className={`flex items-center justify-between p-4 bg-slate-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                            data-testid={`appointment-${apt.id}`}
                          >
                            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${apt.is_teleconsultation ? 'bg-blue-100' : 'bg-primary/10'}`}>
                                {apt.is_teleconsultation ? (
                                  <Video className="w-6 h-6 text-blue-600" />
                                ) : (
                                  <Clock className="w-6 h-6 text-primary" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  Dr. {professional?.first_name} {professional?.last_name}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {apt.date} - {apt.start_time}
                                </p>
                                {apt.is_teleconsultation && (
                                  <Badge variant="outline" className="mt-1 text-xs">Téléconsultation</Badge>
                                )}
                              </div>
                            </div>
                            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              {getStatusBadge(apt.status)}
                              {apt.status === 'scheduled' && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => openChat(apt.professional_id)}
                                  >
                                    <MessageCircle className="w-4 h-4 text-primary" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => handleCancelAppointment(apt.id)}
                                  >
                                    <X className="w-4 h-4 text-destructive" />
                                  </Button>
                                </>
                              )}
                              {apt.status === 'completed' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAppointment(apt);
                                    setShowReview(true);
                                  }}
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Avis
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {appointments.filter(a => a.status !== 'cancelled').length === 0 && (
                        <p className="text-center text-slate-500 py-8">{t('no_appointments')}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      {t('dashboard_profile')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-500">{t('first_name')}</p>
                        <p className="font-medium">{user?.first_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t('email')}</p>
                        <p className="font-medium">{user?.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Abonnement</p>
                        <Badge variant={user?.subscription_type === 'free' ? 'secondary' : 'default'}>
                          {user?.subscription_type === 'free' ? t('pricing_free') : t('pricing_premium_patient')}
                        </Badge>
                      </div>
                      
                      {/* Account Settings */}
                      <div className="pt-4 border-t">
                        <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          {t('account_settings')}
                        </p>
                        <div className="space-y-2">
                          <Link to="/settings/loyalty" className="block">
                            <Button variant="outline" className="w-full justify-start gap-2 border-amber-200 text-amber-700 hover:bg-amber-50">
                              <Trophy className="w-4 h-4" />
                              Programme Fidélité
                            </Button>
                          </Link>
                          <Link to="/settings/notifications" className="block">
                            <Button variant="outline" className="w-full justify-start gap-2">
                              <BellRing className="w-4 h-4" />
                              Notifications
                            </Button>
                          </Link>
                          <Link to="/settings/2fa" className="block">
                            <Button variant="outline" className="w-full justify-start gap-2">
                              <Shield className="w-4 h-4" />
                              Sécurité (2FA)
                            </Button>
                          </Link>
                          <Link to="/settings/invoices" className="block">
                            <Button variant="outline" className="w-full justify-start gap-2">
                              <Receipt className="w-4 h-4" />
                              Mes factures
                            </Button>
                          </Link>
                          <Button 
                            variant="outline" 
                            className="w-full justify-start gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                            onClick={() => setShowDeviceTest(true)}
                            data-testid="test-devices-btn"
                          >
                            <Video className="w-4 h-4" />
                            {t('teleconsultation.test_devices')}
                          </Button>
                          <DataExportButton className="w-full justify-center" variant="outline" />
                          <AccountDeletionButton className="w-full justify-center" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Statistiques</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Total RDV</span>
                        <span className="font-bold text-lg">{appointments.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">{t('waiting')}</span>
                        <span className="font-bold text-lg text-amber-600">{waitlist.length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <Card>
              <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-accent" />
                    {t('dashboard_waitlist')}
                  </CardTitle>
                  <CardDescription>{t('waitlist_desc')}</CardDescription>
                </div>
                <Dialog open={showWaitlist} onOpenChange={setShowWaitlist}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Plus className="w-4 h-4" />
                      {t('waitlist_join')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('waitlist_join')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('waitlist_professional')}</Label>
                        <Select
                          value={waitlistData.professional_id}
                          onValueChange={(value) => setWaitlistData({ ...waitlistData, professional_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {professionals.map((pro) => (
                              <SelectItem key={pro.id} value={pro.id}>
                                Dr. {pro.first_name} {pro.last_name} - {pro.profile?.specialty}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date début</Label>
                          <Input
                            type="date"
                            value={waitlistData.preferred_date_start}
                            onChange={(e) => setWaitlistData({ ...waitlistData, preferred_date_start: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date fin</Label>
                          <Input
                            type="date"
                            value={waitlistData.preferred_date_end}
                            onChange={(e) => setWaitlistData({ ...waitlistData, preferred_date_end: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowWaitlist(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleJoinWaitlist} disabled={!waitlistData.professional_id}>
                        {t('confirm')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {waitlist.map((entry) => {
                    const professional = professionals.find(p => p.id === entry.professional_id);
                    return (
                      <div 
                        key={entry.id}
                        className={`flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Bell className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              Dr. {professional?.first_name} {professional?.last_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {entry.preferred_date_start} - {entry.preferred_date_end}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleLeaveWaitlist(entry.id)}>
                          {t('waitlist_leave')}
                        </Button>
                      </div>
                    );
                  })}
                  {waitlist.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{t('no_waitlist')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  Messages
                </CardTitle>
                <CardDescription>Communiquez avec vos professionnels de santé</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {conversations.map((conv) => (
                    <div 
                      key={conv.user_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                      onClick={() => openChat(conv.user_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{conv.user_name}</p>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{conv.last_message}</p>
                        </div>
                      </div>
                      {conv.unread_count > 0 && (
                        <Badge variant="destructive">{conv.unread_count}</Badge>
                      )}
                    </div>
                  ))}
                  {conversations.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{t('no_conversations')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Forum Tab */}
          <TabsContent value="forum">
            <ForumCommunity token={token} user={user} />
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={showReview} onOpenChange={setShowReview}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Laisser un avis</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label>Note</Label>
                <div className="flex gap-2 mt-2">
                  {[1,2,3,4,5].map((star) => (
                    <Star
                      key={star}
                      className={`w-8 h-8 cursor-pointer ${star <= reviewData.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                      onClick={() => setReviewData({...reviewData, rating: star})}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Commentaire (optionnel)</Label>
                <Textarea
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                  placeholder="{t('review_placeholder')}"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={reviewData.is_anonymous}
                  onChange={(e) => setReviewData({...reviewData, is_anonymous: e.target.checked})}
                />
                <Label htmlFor="anonymous">Avis anonyme</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>{t('cancel')}</Button>
              <Button onClick={handleSubmitReview}>{t('send')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Chat Dialog */}
        <Dialog open={showChat} onOpenChange={setShowChat}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Chat</DialogTitle>
            </DialogHeader>
            <div className="h-80 flex flex-col">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {chatMessages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender_id === user?.id 
                          ? 'bg-primary text-white' 
                          : 'bg-slate-100'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-white/70' : 'text-slate-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex gap-2 p-4 border-t">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Votre message..."
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button size="icon" onClick={sendMessage}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Ad Banner - Only for non-premium patients */}
      {!user?.is_premium && <AdBanner position="bottom" />}
      
      {/* Device Test Modal for Teleconsultation */}
      <DeviceTestModal 
        isOpen={showDeviceTest}
        onClose={() => setShowDeviceTest(false)}
        onSuccess={() => toast.success(t('teleconsultation.test_success'))}
      />
    </div>
  );
};

export default PatientDashboard;
